import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Layout from "../components/Layout";
import { Presentation, FileQuestion, NotebookPen, Sparkles, ArrowRight, Crown, ScanLine } from "lucide-react";

const tools = [
  { to: "/app/presentation", icon: Presentation, title: "Генератор на презентации", desc: "Създай пълна презентация по всяка тема.", color: "bg-sky-500" },
  { to: "/app/worksheet", icon: FileQuestion, title: "Генератор на работни листове", desc: "Тестове, викторини и упражнения.", color: "bg-emerald-500" },
  { to: "/app/lesson-plan", icon: NotebookPen, title: "Резюме на урока", desc: "Подробни планове по програма.", color: "bg-amber-500" },
  { to: "/app/grading", icon: ScanLine, title: "Проверка на тестове", desc: "Снимай тест и AI го оценява.", color: "bg-rose-500" },
];

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [count, setCount] = useState(0);

  useEffect(() => {
    api.get("/resources").then(({ data }) => setCount(data.length)).catch(() => {});
  }, []);

  const isFree = user?.plan === "free";
  const used = user?.ai_usage_count || 0;

  return (
    <Layout>
      <div className="max-w-5xl">
        <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }}>
          <h1 className="font-heading text-3xl sm:text-4xl font-black text-slate-900">Здравей, {user?.name}! 👋</h1>
          <p className="text-slate-500 mt-2 text-lg">Какво ще създадем днес?</p>
        </motion.div>

        <div className="grid sm:grid-cols-3 gap-4 mt-8">
          <div className="bg-white border border-slate-100 rounded-2xl p-6" data-testid="stat-resources">
            <div className="text-3xl font-heading font-black text-slate-900">{count}</div>
            <div className="text-slate-400 text-sm mt-1">Създадени материали</div>
          </div>
          <div className="bg-white border border-slate-100 rounded-2xl p-6" data-testid="stat-usage">
            <div className="text-3xl font-heading font-black text-slate-900">{isFree ? `${used}/5` : "∞"}</div>
            <div className="text-slate-400 text-sm mt-1">Използвани AI заявки</div>
          </div>
          <div className="bg-gradient-to-br from-amber-400 to-amber-500 rounded-2xl p-6 text-white cursor-pointer" onClick={() => navigate("/app/pricing")} data-testid="stat-plan">
            <div className="flex items-center gap-2"><Crown size={24} /><span className="text-2xl font-heading font-black">{isFree ? "Безплатен" : user?.plan === "pro" ? "Pro" : "Училище"}</span></div>
            <div className="text-white/80 text-sm mt-1">{isFree ? "Надгради за неограничен достъп →" : "Активен план"}</div>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-6 mt-6">
          {tools.map((t, i) => (
            <motion.button
              key={t.to} data-testid={`tool-${t.to.split("/").pop()}`}
              initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.08 * i }}
              onClick={() => navigate(t.to)}
              className="group bg-white border border-slate-100 rounded-3xl p-7 text-left shadow-sm hover:-translate-y-1 hover:shadow-lg transition-[transform,box-shadow]"
            >
              <div className={`${t.color} text-white w-12 h-12 rounded-2xl flex items-center justify-center mb-4`}><t.icon size={24} /></div>
              <div className="flex items-center justify-between">
                <h3 className="font-heading font-bold text-xl text-slate-900">{t.title}</h3>
                <ArrowRight size={20} className="text-slate-300 group-hover:text-sky-500 group-hover:translate-x-1 transition-[color,transform]" />
              </div>
              <p className="text-slate-400 text-sm mt-1">{t.desc}</p>
            </motion.button>
          ))}
        </div>
      </div>
    </Layout>
  );
}
